import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Select, MenuItem, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, Grid } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';

export default function FFRecoveryPanel({ formData, setFormData, readOnly }) {
  const [recoveries, setRecoveries] = useState([]);

  useEffect(() => {
    // If formData has recovery_lines, parse them
    if (formData?.recovery_lines) {
      try {
        const parsed = typeof formData.recovery_lines === 'string' ? JSON.parse(formData.recovery_lines) : formData.recovery_lines;
        if (Array.isArray(parsed)) {
          setRecoveries(parsed);
        }
      } catch (e) {
        console.error('Failed to parse recovery lines', e);
      }
    }
  }, [formData?.recovery_lines]);

  const handleMethodChange = (index, method) => {
    if (readOnly) return;
    const updated = [...recoveries];
    updated[index] = { ...updated[index], recovery_method: method };
    setRecoveries(updated);
    if (setFormData) {
      setFormData(prev => ({ ...prev, recovery_lines: JSON.stringify(updated) }));
    }
  };

  const handleCashPaidChange = (index, amount) => {
    if (readOnly) return;
    const updated = [...recoveries];
    const val = Number(amount) || 0;
    updated[index] = { ...updated[index], cash_paid: val, remaining_amount: Math.max(0, updated[index].outstanding_amount - val) };
    setRecoveries(updated);
    if (setFormData) {
      setFormData(prev => ({ ...prev, recovery_lines: JSON.stringify(updated) }));
    }
  };

  const totalOutstanding = recoveries.reduce((acc, curr) => acc + (Number(curr.outstanding_amount) || 0), 0);
  const totalCash = recoveries.reduce((acc, curr) => acc + (Number(curr.cash_paid) || 0), 0);
  const totalPayroll = recoveries.reduce((acc, curr) => {
    if (curr.recovery_method === 'Waive' || curr.recovery_method === 'Cash') return acc;
    return acc + Math.max(0, (Number(curr.outstanding_amount) || 0) - (Number(curr.cash_paid) || 0));
  }, 0);
  const totalWaived = recoveries.reduce((acc, curr) => acc + (curr.recovery_method === 'Waive' ? Math.max(0, (Number(curr.outstanding_amount) || 0) - (Number(curr.cash_paid) || 0)) : 0), 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentsIcon color="primary" /> Outstanding Recoveries
      </Typography>

      <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#F9FAFB' }}>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Reference ID</TableCell>
              <TableCell align="right">Outstanding (₹)</TableCell>
              <TableCell>Recovery Method</TableCell>
              <TableCell align="right">Cash Paid (₹)</TableCell>
              <TableCell align="right">Remaining for Payroll (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recoveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No active recoveries found.
                </TableCell>
              </TableRow>
            ) : (
              recoveries.map((rec, idx) => (
                <TableRow key={idx}>
                  <TableCell>{rec.type || 'Other'}</TableCell>
                  <TableCell>{rec.reference_id || '-'}</TableCell>
                  <TableCell align="right">{Number(rec.outstanding_amount).toLocaleString()}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={rec.recovery_method || 'Payroll'}
                      onChange={(e) => handleMethodChange(idx, e.target.value)}
                      disabled={readOnly}
                      sx={{ width: 140, bgcolor: 'white' }}
                    >
                      <MenuItem value="Payroll">Payroll Deduction</MenuItem>
                      <MenuItem value="Cash">Cash Only</MenuItem>
                      <MenuItem value="Mixed">Mixed (Cash + Payroll)</MenuItem>
                      <MenuItem value="Waive">Waive Completely</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={rec.cash_paid || 0}
                      onChange={(e) => handleCashPaidChange(idx, e.target.value)}
                      disabled={readOnly || rec.recovery_method === 'Payroll' || rec.recovery_method === 'Waive'}
                      sx={{ width: 100, input: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {rec.recovery_method === 'Waive' ? '0 (Waived)' : 
                     rec.recovery_method === 'Cash' ? '0' : 
                     (Math.max(0, (Number(rec.outstanding_amount) || 0) - (Number(rec.cash_paid) || 0))).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
            {recoveries.length > 0 && (
              <TableRow sx={{ bgcolor: '#F3F4F6', '& td': { fontWeight: 'bold' } }}>
                <TableCell colSpan={2}>Totals</TableCell>
                <TableCell align="right">{totalOutstanding.toLocaleString()}</TableCell>
                <TableCell></TableCell>
                <TableCell align="right">{totalCash.toLocaleString()}</TableCell>
                <TableCell align="right">{totalPayroll.toLocaleString()}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentsIcon fontSize="small" /> Cash Payment Entry (Global Offset)
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Total Cash Amount"
              value={formData.cash_received || ''}
              onChange={(e) => setFormData && setFormData(prev => ({ ...prev, cash_received: e.target.value }))}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Select
              fullWidth
              size="small"
              value={formData.payment_mode || 'Cash'}
              onChange={(e) => setFormData && setFormData(prev => ({ ...prev, payment_mode: e.target.value }))}
              disabled={readOnly}
              displayEmpty
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="Bank">Bank Transfer</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Receipt / Reference Number"
              value={formData.receipt_no || ''}
              onChange={(e) => setFormData && setFormData(prev => ({ ...prev, receipt_no: e.target.value }))}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentsIcon fontSize="small" color="secondary" /> Payroll Recovery Controls
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F5F3FF', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Deduction per Payroll (₹)"
              value={formData.max_recovery_per_payroll || ''}
              onChange={(e) => setFormData && setFormData(prev => ({ ...prev, max_recovery_per_payroll: e.target.value }))}
              disabled={readOnly}
              helperText="Leave empty to recover all in next run"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Select
              fullWidth
              size="small"
              value={formData.auto_split_enabled || 'Y'}
              onChange={(e) => setFormData && setFormData(prev => ({ ...prev, auto_split_enabled: e.target.value }))}
              disabled={readOnly}
              displayEmpty
            >
              <MenuItem value="Y">Auto Split Enabled</MenuItem>
              <MenuItem value="N">Do Not Split</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Installment Count (Calculated)"
              value={
                formData.max_recovery_per_payroll && totalPayroll > 0
                  ? Math.ceil(totalPayroll / Number(formData.max_recovery_per_payroll))
                  : (totalPayroll > 0 ? 1 : 0)
              }
              disabled
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
